import {makeAutoObservable, runInAction} from 'mobx';
import type {GetUserRepScoreUseCase} from '../../core/application/use-cases/repScore/GetUserRepScoreUseCase';
import type {
    RepScoreService,
    RepScoreResponse,
} from '../../core/infrastructure/services/RepScoreService';

export class RepScoreStore {
    // State
    repScore: RepScoreResponse | null = null;
    isLoading = false;
    error: string | null = null;

    constructor(
        private readonly getUserRepScoreUseCase: GetUserRepScoreUseCase,
        private readonly repScoreService: RepScoreService
    ) {
        makeAutoObservable(this);
    }

    // Computed values
    get hasRepScore(): boolean {
        return this.repScore !== null;
    }

    get isPSSEligible(): boolean {
        return this.repScore?.pssEligible || false;
    }

    get currentScore(): number {
        return this.repScore?.currentScore || 0;
    }

    get scoreLevel(): string {
        return this.repScore?.scoreLevel || 'unknown';
    }

    get maxPSSAmount(): string {
        return this.repScore?.maxPssAmount || '0';
    }

    get pssCreditPercentage(): string {
        return this.repScore?.pssCreditPercentage || '0';
    }

    get nextLevelThreshold(): number {
        return this.repScore?.nextLevelThreshold || 0;
    }

    get progressToNextLevel(): number {
        if (!this.repScore) return 0;
        const current = this.repScore.currentScore;
        const next = this.repScore.nextLevelThreshold;
        const previous = this.getPreviousLevelThreshold();

        if (next === current) return 100;
        return ((current - previous) / (next - previous)) * 100;
    }

    get scoreBreakdown() {
        return this.repScore?.scoreBreakdown || null;
    }

    get recentScoreChanges() {
        return this.repScore?.scoreHistory?.slice(0, 5) || [];
    }

    // Actions
    async loadUserRepScore(userId: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const repScore = await this.getUserRepScoreUseCase.execute({userId});

            runInAction(() => {
                this.repScore = repScore;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load RepScore');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async refreshRepScore(userId: string): Promise<void> {
        await this.loadUserRepScore(userId);
    }

    // Helper methods
    getScoreColor(): string {
        if (!this.repScore) return '#gray';

        const score = this.repScore.currentScore;
        if (score >= 800) return '#22c55e'; // green
        if (score >= 700) return '#3b82f6'; // blue
        if (score >= 600) return '#f59e0b'; // yellow
        if (score >= 500) return '#f97316'; // orange
        return '#ef4444'; // red
    }

    getScoreGrade(): string {
        if (!this.repScore) return 'N/A';

        const score = this.repScore.currentScore;
        if (score >= 800) return 'Excellent';
        if (score >= 700) return 'Good';
        if (score >= 600) return 'Fair';
        if (score >= 500) return 'Poor';
        return 'Very Poor';
    }

    canImproveScore(): boolean {
        if (!this.repScore) return false;

        const breakdown = this.repScore.scoreBreakdown;
        return (
            breakdown.profileCompletion.completionPercentage < 100 ||
            breakdown.profileCompletion.missingVerifications.length > 0
        );
    }

    getImprovementSuggestions(): string[] {
        if (!this.repScore) return [];

        const suggestions: string[] = [];
        const breakdown = this.repScore.scoreBreakdown;

        if (breakdown.profileCompletion.completionPercentage < 100) {
            suggestions.push('Complete your profile information');
        }

        if (breakdown.profileCompletion.missingVerifications.length > 0) {
            suggestions.push(`Complete ${breakdown.profileCompletion.missingVerifications.join(', ')} verification`);
        }

        if (breakdown.paymentHistory.successRate < 95) {
            suggestions.push('Maintain consistent payment history');
        }

        if (breakdown.purchaseBehavior.totalOrders < 5) {
            suggestions.push('Build purchase history with more orders');
        }

        return suggestions;
    }

    private getPreviousLevelThreshold(): number {
        if (!this.repScore) return 0;

        const current = this.repScore.currentScore;
        if (current >= 800) return 700;
        if (current >= 700) return 600;
        if (current >= 600) return 500;
        if (current >= 500) return 400;
        return 0;
    }

    private setLoading(loading: boolean): void {
        this.isLoading = loading;
    }

    private setError(error: string | null): void {
        this.error = error;
    }
}
